import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Choice } from '@/components/Choice'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { api, type Me } from '@/lib/api'
import { isMotion, isTheme, type Preferences as Prefs, type Motion, type Theme } from '../../shared/preferences'

const THEME_CHOICES: [Theme, string][] = [
  ['light', 'Light'],
  ['dark', 'Dark'],
  ['system', 'Match my device'],
]

const MOTION_CHOICES: [Motion, string][] = [
  ['smooth', 'Smooth'],
  ['simple', 'Simple'],
]

// How gatheredfrom looks and behaves for you, on your profile. Saved to your account, so it's the same on every device.
export function Preferences({ me }: { me: Me }) {
  const queryClient = useQueryClient()
  const save = useMutation({
    mutationFn: (change: Partial<Prefs>) => api('/me/preferences', { method: 'PATCH', body: change }),
    // The page changes the moment you choose; if saving fails it goes back to what the account has.
    onMutate: async (change) => {
      await queryClient.cancelQueries({ queryKey: ['me'] })
      const before = me.preferences
      queryClient.setQueryData<Me>(['me'], (m) => m && { ...m, preferences: { ...m.preferences, ...change } })
      return { before }
    },
    onError: (_error, _change, context) =>
      queryClient.setQueryData<Me>(['me'], (m) => m && context && { ...m, preferences: context.before }),
  })

  return (
    <section className="flex flex-col gap-6 border-t pt-8" aria-labelledby="preferences">
      <h2 id="preferences" className="text-title">
        Preferences
      </h2>
      <Choice
        legend="Appearance"
        hint="Saved to your account, so it follows you to every device."
        value={me.preferences.theme}
        options={THEME_CHOICES}
        onChange={(v) => isTheme(v) && save.mutate({ theme: v })}
      />
      <Choice
        legend="Motion"
        hint={
          me.preferences.motion === 'simple'
            ? 'Quick fades from one thing to the next, and nothing that travels.'
            : 'Photos grow from where they sit and slide from one to the next.'
        }
        value={me.preferences.motion}
        options={MOTION_CHOICES}
        onChange={(v) => isMotion(v) && save.mutate({ motion: v })}
      />
      {save.error && (
        <Alert variant="destructive">
          <AlertDescription>{save.error.message}</AlertDescription>
        </Alert>
      )}
    </section>
  )
}
